import { useEffect, useRef, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, type LucideIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { revealStyle, useScrollReveal } from '@/hooks/useScrollReveal';

/**
 * Building blocks for the three role pages (spec 010 §4: Real Screen, Straight Answers, Close).
 * Each audience gets its own accent and its own composition; the kit only holds what must not drift:
 * the Example caption, the once-only scroll reveal, and the accordion behaviour.
 */
export type Audience = 'students' | 'parents' | 'tutors';

export const ACCENTS: Record<
  Audience,
  { text: string; tint: string; border: string; rule: string; mdRule: string; dot: string; chip: string }
> = {
  // Students carry the brand burgundy.
  students: {
    text: 'text-primary',
    tint: 'bg-primary/[0.05]',
    border: 'border-primary/20',
    rule: 'border-primary/40',
    mdRule: 'md:border-primary/40',
    dot: 'bg-primary',
    chip: 'bg-primary/10 text-primary',
  },
  // Parents: a calm sage, the colour of "all good".
  parents: {
    text: 'text-emerald-800 dark:text-emerald-300',
    tint: 'bg-emerald-600/[0.06]',
    border: 'border-emerald-700/20 dark:border-emerald-400/20',
    rule: 'border-emerald-700/40 dark:border-emerald-400/40',
    mdRule: 'md:border-emerald-700/40 md:dark:border-emerald-400/40',
    dot: 'bg-emerald-600 dark:bg-emerald-400',
    chip: 'bg-emerald-600/10 text-emerald-800 dark:text-emerald-300',
  },
  // Tutors: working-tool slate blue.
  tutors: {
    text: 'text-sky-800 dark:text-sky-300',
    tint: 'bg-sky-700/[0.06]',
    border: 'border-sky-700/20 dark:border-sky-400/20',
    rule: 'border-sky-700/40 dark:border-sky-400/40',
    mdRule: 'md:border-sky-700/40 md:dark:border-sky-400/40',
    dot: 'bg-sky-700 dark:bg-sky-400',
    chip: 'bg-sky-700/10 text-sky-800 dark:text-sky-300',
  },
};

export interface Callout {
  title: string;
  body: string;
  icon?: LucideIcon;
}

export type RealScreenLayout = 'rail-left' | 'rail-right' | 'below' | 'band';

function CalloutList({
  callouts,
  audience,
  className,
}: {
  callouts: readonly Callout[];
  audience: Audience;
  className?: string;
}) {
  const a = ACCENTS[audience];
  return (
    <ul className={className} data-testid="callouts">
      {callouts.map(({ title, body, icon: Icon }) => (
        <li key={title} className={cn('border-l-2 pl-4', a.rule)}>
          <p className="flex items-center gap-2 font-semibold">
            {Icon && <Icon className={cn('h-4 w-4 shrink-0', a.text)} aria-hidden />}
            {title}
          </p>
          <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{body}</p>
        </li>
      ))}
    </ul>
  );
}

/**
 * Real Screen (spec 010 §4 D): one large surface, 2-3 static callouts, an `Example …` caption,
 * revealed once on scroll. The surface is passed as children.
 */
export function RealScreen({
  id,
  audience,
  layout,
  heading,
  lede,
  callouts,
  caption,
  children,
}: {
  id: string;
  audience: Audience;
  layout: RealScreenLayout;
  heading: string;
  lede?: string;
  callouts: readonly Callout[];
  /** Must start with "Example". */
  caption: string;
  children: ReactNode;
}) {
  const { ref, isVisible } = useScrollReveal(0.08);
  const a = ACCENTS[audience];
  const titleId = `${id}-title`;

  const surface = (
    <figure className="min-w-0">
      <div className={cn('rounded-2xl border p-3 sm:p-5', a.tint, a.border)}>{children}</div>
      <figcaption className="mt-3 text-sm text-muted-foreground">{caption}</figcaption>
    </figure>
  );

  const head = (
    <header className={cn(layout === 'band' && 'mx-auto max-w-2xl text-center')}>
      <h2 id={titleId} className="text-balance text-2xl font-semibold tracking-tight sm:text-3xl lg:text-4xl">
        {heading}
      </h2>
      {lede && <p className="mt-3 max-w-prose text-pretty text-base leading-relaxed text-muted-foreground sm:text-lg">{lede}</p>}
    </header>
  );

  return (
    <section
      id={id}
      aria-labelledby={titleId}
      className={cn('scroll-mt-20', layout === 'band' ? cn('border-y', a.tint, a.border) : '')}
    >
      <div className="mx-auto w-full max-w-6xl px-4 py-12 sm:px-6 lg:py-16">
        <div ref={ref} style={revealStyle(isVisible)}>
          {layout === 'band' && (
            <div className="flex flex-col gap-8">
              {head}
              <div className="mx-auto w-full max-w-4xl">
                <figure className="min-w-0">
                  <div className="rounded-2xl border bg-background p-3 shadow-sm sm:p-5">{children}</div>
                  <figcaption className="mt-3 text-center text-sm text-muted-foreground">{caption}</figcaption>
                </figure>
              </div>
              <CalloutList callouts={callouts} audience={audience} className="grid gap-6 md:grid-cols-3" />
            </div>
          )}

          {layout === 'below' && (
            <div className="flex flex-col gap-8">
              {head}
              {surface}
              <CalloutList callouts={callouts} audience={audience} className="grid gap-6 md:grid-cols-3" />
            </div>
          )}

          {(layout === 'rail-left' || layout === 'rail-right') && (
            <div
              className={cn(
                'grid items-start gap-8 lg:gap-12',
                layout === 'rail-right'
                  ? 'lg:grid-cols-[minmax(0,1fr)_minmax(0,19rem)]'
                  : 'lg:grid-cols-[minmax(0,19rem)_minmax(0,1fr)]',
              )}
            >
              <div className={cn('flex flex-col gap-7', layout === 'rail-right' && 'lg:order-2')}>
                {head}
                <CalloutList callouts={callouts} audience={audience} className="grid gap-5" />
              </div>
              <div className={cn(layout === 'rail-right' && 'lg:order-1')}>{surface}</div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

export interface Answer {
  q: string;
  a: ReactNode;
}

/**
 * Straight Answers (spec 010 §4 E): 3-5 plain Q/As. Prerendered open so the answers are in the HTML;
 * collapses to an accordion at phone width on hydrate.
 */
export function RoleAnswers({
  audience,
  heading = 'Straight answers',
  answers,
}: {
  audience: Audience;
  heading?: string;
  answers: readonly Answer[];
}) {
  const a = ACCENTS[audience];
  const listRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (typeof window.matchMedia !== 'function' || !window.matchMedia('(max-width: 767px)').matches) return;
    listRef.current?.querySelectorAll('details').forEach((d) => d.removeAttribute('open'));
  }, []);

  return (
    <section aria-labelledby={`${audience}-answers-title`} className="mx-auto w-full max-w-6xl px-4 py-12 sm:px-6 lg:py-16">
      <div className="grid gap-8 lg:grid-cols-[minmax(0,16rem)_minmax(0,1fr)] lg:gap-16">
        <h2 id={`${audience}-answers-title`} className="text-2xl font-semibold tracking-tight sm:text-3xl">
          {heading}
        </h2>
        <div ref={listRef} className="grid gap-x-12 gap-y-2 md:grid-cols-2 md:gap-y-7" data-testid="straight-answers">
          {answers.map(({ q, a: answer }) => (
            <details key={q} open className={cn('group border-b py-1 md:border-b-0 md:border-l-2 md:py-0 md:pl-4', a.mdRule)}>
              <summary className="flex min-h-11 cursor-pointer list-none items-center justify-between gap-3 rounded text-base font-semibold outline-none marker:hidden focus-visible:ring-2 focus-visible:ring-primary md:pointer-events-none md:min-h-0 [&::-webkit-details-marker]:hidden">
                {q}
                <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-open:rotate-180 md:hidden" aria-hidden />
              </summary>
              <div className="max-w-prose pb-3 pt-1 text-[15px] leading-relaxed text-muted-foreground md:pb-0">{answer}</div>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

/** Close: one primary, one quiet alternative. */
export function RoleClose({
  audience,
  heading,
  body,
  primary,
  secondary,
}: {
  audience: Audience;
  heading: string;
  body: string;
  primary: { label: string; href: string };
  secondary?: { label: string; to: string; external?: boolean };
}) {
  const a = ACCENTS[audience];
  return (
    <section aria-labelledby={`${audience}-close-title`} className="px-4 pb-16 sm:px-6 lg:pb-24">
      <div className={cn('mx-auto flex max-w-6xl flex-col gap-6 rounded-3xl border px-6 py-10 sm:px-10 lg:flex-row lg:items-center lg:justify-between lg:py-12', a.tint, a.border)}>
        <div className="max-w-xl">
          <h2 id={`${audience}-close-title`} className="text-balance text-3xl font-bold tracking-tight sm:text-4xl">
            {heading}
          </h2>
          <p className="mt-3 text-base leading-relaxed text-muted-foreground">{body}</p>
        </div>
        <div className="flex flex-col items-stretch gap-3 sm:flex-row sm:items-center sm:gap-5">
          <Button asChild size="lg" className="h-12 px-8 text-base">
            <Link to={primary.href}>{primary.label}</Link>
          </Button>
          {secondary &&
            (secondary.external ? (
              <a href={secondary.to} className="self-center rounded text-sm font-medium underline underline-offset-4 hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary">
                {secondary.label}
              </a>
            ) : (
              <Link to={secondary.to} className="self-center rounded text-sm font-medium underline underline-offset-4 hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary">
                {secondary.label}
              </Link>
            ))}
        </div>
      </div>
    </section>
  );
}

/** Head tags for a role page (title, description, canonical, Open Graph, Twitter). */
export function RoleHead({
  title,
  description,
  keywords,
  path,
}: {
  title: string;
  description: string;
  keywords: string;
  path: string;
}) {
  const url = `https://mentaratutors.com${path}`;
  const image = 'https://mentaratutors.com/logos/mentara-full-light.png';
  return (
    <>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <link rel="canonical" href={url} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:url" content={url} />
      <meta property="twitter:title" content={title} />
      <meta property="twitter:description" content={description} />
      <meta property="twitter:image" content={image} />
    </>
  );
}
